import React, { useState, useEffect, useCallback } from 'react';
import './ApplicationForm.css';
import './Dashboard.css';
import { getEducationLabel } from './steps/MaritalHistory';

const ApplicationReview = ({ applicationId, user, onExit }) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchApplicationData = useCallback(async () => {
    try {
      const response = await fetch(`/OnlineMarriageLicense/api/applications/${applicationId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const application = data.application || data;

        // Parse form_data if it's a string
        let parsedFormData = application.form_data;
        if (typeof parsedFormData === 'string') {
          parsedFormData = JSON.parse(parsedFormData);
        }

        // Handle wrapped format { currentStep, formData }
        const actualFormData = parsedFormData?.formData || parsedFormData;

        if (actualFormData) {
          setFormData(actualFormData);
        } else {
          setError('No form data found in application');
        }
      } else {
        setError('Failed to load application data');
      }
    } catch (err) {
      console.error('Error fetching application:', err);
      setError('An error occurred while loading your application');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    fetchApplicationData();
  }, [fetchApplicationData]);

  if (loading) {
    return (
      <div className="application-form-container">
        <div className="application-review-state">
          <p>Loading application...</p>
        </div>
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div className="application-form-container">
        <div className="application-review-state">
          <p className="application-review-error">{error || 'Application not found'}</p>
          <button onClick={onExit} className="btn btn-primary">Return to Dashboard</button>
        </div>
      </div>
    );
  }

  const partyA = formData.partyA || {};
  const partyB = formData.partyB || {};
  const partyAMaritalHistory = partyA.maritalHistory || {};
  const partyBMaritalHistory = partyB.maritalHistory || {};

  return (
    <div className="application-form-container">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo">
            <div className="logo-icon">
              <img
                src={`${process.env.PUBLIC_URL}/JPCC_seal_trans_white.png`}
                alt="Jefferson Parish Clerk of Court Seal"
              />
            </div>
            <div className="logo-text">
              <h1>Jon A. Gegenheimer</h1>
              <p>Jefferson Parish Clerk of Court</p>
              <p className="logo-context">Marriage License Pre-Application</p>
            </div>
          </div>

          <div className="user-menu">
            <button onClick={onExit} className="logout-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="application-content">
        <div className="step-container application-review-container">

          {/* Application Status Banner */}
          <div className="application-status-panel">
            <h3>Application Submitted</h3>
            <p>
              Your application has been submitted and is being processed. You cannot make changes at this time.
              Please visit our office with required documents to complete the process.
            </p>
          </div>

          {/* Marriage Type */}
          {(formData.isCovenantMarriage !== null || formData.isConsanguineous !== null) && (
            <div>
              <h2 className="form-section-title">Marriage Information</h2>
              <div className="review-section">
                {formData.isCovenantMarriage !== null && (
                  <div className="review-item">
                    <div className="review-label">Covenant Marriage:</div>
                    <div className="review-value">{formData.isCovenantMarriage ? 'Yes' : 'No'}</div>
                  </div>
                )}
                {formData.isConsanguineous !== null && (
                  <div className="review-item">
                    <div className="review-label">Related by Blood:</div>
                    <div className="review-value">{formData.isConsanguineous ? 'Yes' : 'No'}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Party A Information */}
          <div className="application-review-party-section">
            <h1 className="form-section-title">{partyA.role || 'Party A'} Information</h1>

            {/* Basic Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Party Type & Sex</h3>
              <div className="review-item">
                <div className="review-label">Party Type:</div>
                <div className="review-value">{partyA.role || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Sex:</div>
                <div className="review-value">{partyA.sex || 'Not provided'}</div>
              </div>
            </div>

            {/* Name Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Name Information</h3>
              <div className="review-item">
                <div className="review-label">First Name:</div>
                <div className="review-value">{partyA.firstName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Middle Name:</div>
                <div className="review-value">{partyA.middleName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Last Name:</div>
                <div className="review-value">{partyA.lastName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Suffix:</div>
                <div className="review-value">{partyA.suffix || 'None'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Last Name Before Marriage:</div>
                <div className="review-value">{partyA.lastNameBeforeMarriage || 'Not provided'}</div>
              </div>
            </div>

            {/* Residence Address */}
            <div className="review-section">
              <h3 className="form-subsection-title">Residence Address</h3>
              <div className="review-item">
                <div className="review-label">Address:</div>
                <div className="review-value">{partyA.residenceAddress || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">City:</div>
                <div className="review-value">{partyA.city || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Parish/County:</div>
                <div className="review-value">{partyA.parish || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">State:</div>
                <div className="review-value">{partyA.state || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">ZIP:</div>
                <div className="review-value">{partyA.zip || 'Not provided'}</div>
              </div>
            </div>

            {/* Birth Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Birth Information</h3>
              <div className="review-item">
                <div className="review-label">Race:</div>
                <div className="review-value">{partyA.race || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Date of Birth:</div>
                <div className="review-value">{partyA.dateOfBirth || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - City:</div>
                <div className="review-value">{partyA.birthCity || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - State:</div>
                <div className="review-value">{partyA.birthState || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - Country:</div>
                <div className="review-value">{partyA.birthCountry || 'Not provided'}</div>
              </div>
            </div>

            {/* Mother/Parent Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Mother/Parent Information</h3>
              <div className="review-item">
                <div className="review-label">Mother/Parent's Name:</div>
                <div className="review-value">{partyA.motherName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - City:</div>
                <div className="review-value">{partyA.motherBirthCity || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - State:</div>
                <div className="review-value">{partyA.motherBirthState || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - Country:</div>
                <div className="review-value">{partyA.motherBirthCountry || 'Not provided'}</div>
              </div>
            </div>

            {/* Father/Parent Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Father/Parent Information</h3>
              <div className="review-item">
                <div className="review-label">Father/Parent's Name:</div>
                <div className="review-value">{partyA.fatherName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - City:</div>
                <div className="review-value">{partyA.fatherBirthCity || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - State:</div>
                <div className="review-value">{partyA.fatherBirthState || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - Country:</div>
                <div className="review-value">{partyA.fatherBirthCountry || 'Not provided'}</div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Contact Information</h3>
              <div className="review-item">
                <div className="review-label">Social Security Number:</div>
                <div className="review-value">
                  {partyA.ssn ? `***-**-${partyA.ssn.slice(-4)}` : 'Not provided'}
                </div>
              </div>
              <div className="review-item">
                <div className="review-label">SSN Confidential:</div>
                <div className="review-value">{partyA.ssnConfidential ? 'Yes' : 'No'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Phone Number:</div>
                <div className="review-value">{partyA.phoneNumber || 'Not provided'}</div>
              </div>
            </div>

            {/* Marital History */}
            <div className="review-section">
              <h3 className="form-subsection-title">Marital History & Education</h3>
              <div className="review-item">
                <div className="review-label">Number of Previous Marriages:</div>
                <div className="review-value">{partyAMaritalHistory.previousMarriages || '0'}</div>
              </div>
              {partyAMaritalHistory.previousMarriages && parseInt(partyAMaritalHistory.previousMarriages) > 0 && (
                <>
                  <div className="review-item">
                    <div className="review-label">Date Last Marriage Ended:</div>
                    <div className="review-value">{partyAMaritalHistory.lastMarriageEndDate || 'Not provided'}</div>
                  </div>
                  <div className="review-item">
                    <div className="review-label">Reason Last Marriage Ended:</div>
                    <div className="review-value">{partyAMaritalHistory.lastMarriageEndReason || 'Not provided'}</div>
                  </div>
                </>
              )}
              <div className="review-item">
                <div className="review-label">Highest Education Completed:</div>
                <div className="review-value">{getEducationLabel(partyAMaritalHistory.education)}</div>
              </div>
            </div>
          </div>

          {/* Party B Information */}
          <div className="application-review-party-section application-review-party-section-spaced">
            <h1 className="form-section-title">{partyB.role || 'Party B'} Information</h1>

            {/* Basic Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Party Type & Sex</h3>
              <div className="review-item">
                <div className="review-label">Party Type:</div>
                <div className="review-value">{partyB.role || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Sex:</div>
                <div className="review-value">{partyB.sex || 'Not provided'}</div>
              </div>
            </div>

            {/* Name Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Name Information</h3>
              <div className="review-item">
                <div className="review-label">First Name:</div>
                <div className="review-value">{partyB.firstName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Middle Name:</div>
                <div className="review-value">{partyB.middleName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Last Name:</div>
                <div className="review-value">{partyB.lastName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Suffix:</div>
                <div className="review-value">{partyB.suffix || 'None'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Last Name Before Marriage:</div>
                <div className="review-value">{partyB.lastNameBeforeMarriage || 'Not provided'}</div>
              </div>
            </div>

            {/* Residence Address */}
            <div className="review-section">
              <h3 className="form-subsection-title">Residence Address</h3>
              <div className="review-item">
                <div className="review-label">Address:</div>
                <div className="review-value">{partyB.residenceAddress || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">City:</div>
                <div className="review-value">{partyB.city || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Parish/County:</div>
                <div className="review-value">{partyB.parish || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">State:</div>
                <div className="review-value">{partyB.state || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">ZIP:</div>
                <div className="review-value">{partyB.zip || 'Not provided'}</div>
              </div>
            </div>

            {/* Birth Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Birth Information</h3>
              <div className="review-item">
                <div className="review-label">Race:</div>
                <div className="review-value">{partyB.race || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Date of Birth:</div>
                <div className="review-value">{partyB.dateOfBirth || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - City:</div>
                <div className="review-value">{partyB.birthCity || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - State:</div>
                <div className="review-value">{partyB.birthState || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - Country:</div>
                <div className="review-value">{partyB.birthCountry || 'Not provided'}</div>
              </div>
            </div>

            {/* Mother/Parent Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Mother/Parent Information</h3>
              <div className="review-item">
                <div className="review-label">Mother/Parent's Name:</div>
                <div className="review-value">{partyB.motherName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - City:</div>
                <div className="review-value">{partyB.motherBirthCity || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - State:</div>
                <div className="review-value">{partyB.motherBirthState || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - Country:</div>
                <div className="review-value">{partyB.motherBirthCountry || 'Not provided'}</div>
              </div>
            </div>

            {/* Father/Parent Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Father/Parent Information</h3>
              <div className="review-item">
                <div className="review-label">Father/Parent's Name:</div>
                <div className="review-value">{partyB.fatherName || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - City:</div>
                <div className="review-value">{partyB.fatherBirthCity || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - State:</div>
                <div className="review-value">{partyB.fatherBirthState || 'Not provided'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Place of Birth - Country:</div>
                <div className="review-value">{partyB.fatherBirthCountry || 'Not provided'}</div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="review-section">
              <h3 className="form-subsection-title">Contact Information</h3>
              <div className="review-item">
                <div className="review-label">Social Security Number:</div>
                <div className="review-value">
                  {partyB.ssn ? `***-**-${partyB.ssn.slice(-4)}` : 'Not provided'}
                </div>
              </div>
              <div className="review-item">
                <div className="review-label">SSN Confidential:</div>
                <div className="review-value">{partyB.ssnConfidential ? 'Yes' : 'No'}</div>
              </div>
              <div className="review-item">
                <div className="review-label">Phone Number:</div>
                <div className="review-value">{partyB.phoneNumber || 'Not provided'}</div>
              </div>
            </div>

            {/* Marital History */}
            <div className="review-section">
              <h3 className="form-subsection-title">Marital History & Education</h3>
              <div className="review-item">
                <div className="review-label">Number of Previous Marriages:</div>
                <div className="review-value">{partyBMaritalHistory.previousMarriages || '0'}</div>
              </div>
              {partyBMaritalHistory.previousMarriages && parseInt(partyBMaritalHistory.previousMarriages) > 0 && (
                <>
                  <div className="review-item">
                    <div className="review-label">Date Last Marriage Ended:</div>
                    <div className="review-value">{partyBMaritalHistory.lastMarriageEndDate || 'Not provided'}</div>
                  </div>
                  <div className="review-item">
                    <div className="review-label">Reason Last Marriage Ended:</div>
                    <div className="review-value">{partyBMaritalHistory.lastMarriageEndReason || 'Not provided'}</div>
                  </div>
                </>
              )}
              <div className="review-item">
                <div className="review-label">Highest Education Completed:</div>
                <div className="review-value">{getEducationLabel(partyBMaritalHistory.education)}</div>
              </div>
            </div>
          </div>

          {/* Return Button */}
          <div className="application-review-actions">
            <button onClick={onExit} className="btn btn-primary">
              Return to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ApplicationReview;
